import { apiFetch, hasLiveApi } from '@/api/client'
import type { FpAuthorisePage, FpPageFeature } from '@/features/users/fpAuthorise'
import { filterStaffFormPageFeatures } from '@/features/users/fpAuthorise'

export type BookmakerSessionFlag = 'all' | 'onlyBookmaker' | 'onlySession' | 'bookmakerSession'

export type PermissionAction = { code: string; label: string }
export type PermissionRole = { accessRole: string; actions: PermissionAction[] }
/** CSV: Menu → Access Role (tab) → functionalities */
export type PermissionMenu = { menu: string; roles: PermissionRole[] }

export type StaffUser = {
  id: string
  ownerId: string
  username: string
  status: 'active' | 'inactive'
  bookmakerSessionFlag: BookmakerSessionFlag
  grants: string[]
  accessRoleName?: string | null
  authorise?: FpAuthorisePage[]
  hasPassword: boolean
  createdAt: string
  updatedAt: string
}

export type StaffUserList = {
  results: StaffUser[]
  total: number
  pageSize: number
}

export type CreateStaffUserBody = {
  username: string
  password?: string
  status?: 'active' | 'inactive'
  bookmakerSessionFlag?: BookmakerSessionFlag
  grants?: string[]
  accessRoleName?: string
  authorise?: FpAuthorisePage[]
}

export type UpdateStaffUserBody = {
  password?: string
  status?: 'active' | 'inactive'
  bookmakerSessionFlag?: BookmakerSessionFlag
  grants?: string[]
  accessRoleName?: string
  authorise?: FpAuthorisePage[]
}

export type FpRoleslist = {
  roleName: string
  pageFeatures: FpPageFeature[]
}

const DEMO_MENUS: PermissionMenu[] = [
  {
    menu: 'Member',
    roles: [
      {
        accessRole: 'member list',
        actions: [
          { code: 'downline.view', label: 'View Downline' },
          { code: 'credit-transfer', label: 'Give/Take' },
          { code: 'create-downline', label: 'Create Downline' },
          { code: 'edit-downline', label: 'Edit Downline' },
          { code: 'lock', label: 'Member Lock/Unlock' },
        ],
      },
      {
        accessRole: 'Hierarchy Search',
        actions: [{ code: 'hierarchy-search.view', label: 'Hierarchy list' }],
      },
    ],
  },
  {
    menu: 'Book Management',
    roles: [
      {
        accessRole: 'Bet Ticker',
        actions: [
          { code: 'books.view', label: 'list bets' },
          { code: 'ticker.flag', label: 'flag bet' },
          { code: 'ticker.comment', label: 'comment bet' },
        ],
      },
    ],
  },
]

const DEMO_ROLESLIST: FpRoleslist = {
  roleName: 'Triple Master',
  pageFeatures: DEMO_MENUS.flatMap((menu) =>
    menu.roles.map((role) => ({
      pageName: role.accessRole,
      url: `/${role.accessRole.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
      menu: menu.menu,
      feature: role.actions.map((a) => a.label),
      features: role.actions.map((a) => ({ label: a.label, code: a.code })),
    })),
  ),
}

let demoUsers: StaffUser[] = [
  {
    id: 'b0000000-0000-4000-8000-000000000001',
    ownerId: 'demo-owner',
    username: 'ops.desk',
    status: 'active',
    bookmakerSessionFlag: 'all',
    grants: ['downline.view', 'credit-transfer', 'books.view', 'statements.view'],
    accessRoleName: 'Triple Master',
    authorise: [],
    hasPassword: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
]

function grantsOf(row: StaffUser & { allowAccess?: unknown }): string[] {
  if (Array.isArray(row.grants) && row.grants.every((g) => typeof g === 'string')) {
    return row.grants
  }
  return []
}

function authoriseOf(row: StaffUser): FpAuthorisePage[] {
  if (!Array.isArray(row.authorise)) return []
  return row.authorise.filter(
    (p) =>
      p &&
      typeof p.pageName === 'string' &&
      Array.isArray(p.feature) &&
      p.feature.every((f) => typeof f === 'string'),
  )
}

export function staffUsersLive(): boolean {
  return hasLiveApi()
}

export async function listAccessPermissions(): Promise<PermissionMenu[]> {
  if (!staffUsersLive()) return DEMO_MENUS
  const res = await apiFetch<{ status: number; message: string; data: PermissionMenu[] }>(
    '/v1/access-permissions',
    { method: 'POST', body: JSON.stringify({}) },
  )
  return Array.isArray(res.data) ? res.data : []
}

export async function getStaffRoleslist(roleName: string): Promise<FpRoleslist> {
  if (!staffUsersLive()) {
    return {
      ...DEMO_ROLESLIST,
      pageFeatures: filterStaffFormPageFeatures(DEMO_ROLESLIST.pageFeatures),
    }
  }
  const res = await apiFetch<{ status: number; message: string; data: FpRoleslist }>(
    '/v1/staff/roleslists/get',
    { method: 'POST', body: JSON.stringify({ roleName }) },
  )
  const data = res.data ?? { roleName, pageFeatures: [] as FpPageFeature[] }
  return {
    ...data,
    pageFeatures: filterStaffFormPageFeatures(data.pageFeatures ?? []),
  }
}

export async function listStaffUsers(query: {
  page?: number
  keyword?: string
}): Promise<StaffUserList> {
  if (!staffUsersLive()) {
    const keyword = (query.keyword ?? '').trim().toLowerCase()
    const filtered = keyword
      ? demoUsers.filter((u) => u.username.toLowerCase().includes(keyword))
      : demoUsers
    const page = Math.max(1, query.page ?? 1)
    const pageSize = 20
    const start = (page - 1) * pageSize
    return {
      results: filtered.slice(start, start + pageSize),
      total: filtered.length,
      pageSize,
    }
  }
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.keyword) params.set('keyword', query.keyword)
  const qs = params.toString()
  const data = await apiFetch<StaffUserList>(`/v1/staff-users${qs ? `?${qs}` : ''}`)
  return {
    ...data,
    results: data.results.map((r) => ({
      ...r,
      grants: grantsOf(r),
      authorise: authoriseOf(r),
    })),
  }
}

export async function getStaffUser(id: string): Promise<StaffUser> {
  if (!staffUsersLive()) {
    const found = demoUsers.find((u) => u.id === id)
    if (!found) throw new Error('staff user not found')
    return found
  }
  const row = await apiFetch<StaffUser>(`/v1/staff-users/${id}`)
  return { ...row, grants: grantsOf(row), authorise: authoriseOf(row) }
}

export async function createStaffUser(body: CreateStaffUserBody): Promise<StaffUser> {
  if (!staffUsersLive()) {
    const now = new Date().toISOString()
    const created: StaffUser = {
      id: crypto.randomUUID(),
      ownerId: 'demo-owner',
      username: body.username,
      status: body.status ?? 'active',
      bookmakerSessionFlag: body.bookmakerSessionFlag ?? 'all',
      grants: body.grants ?? [],
      accessRoleName: body.accessRoleName ?? 'Triple Master',
      authorise: body.authorise ?? [],
      hasPassword: Boolean(body.password),
      createdAt: now,
      updatedAt: now,
    }
    demoUsers = [...demoUsers, created]
    return created
  }
  const row = await apiFetch<StaffUser>('/v1/staff-users', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return { ...row, grants: grantsOf(row), authorise: authoriseOf(row) }
}

export async function updateStaffUser(
  id: string,
  body: UpdateStaffUserBody,
): Promise<StaffUser> {
  if (!staffUsersLive()) {
    const idx = demoUsers.findIndex((u) => u.id === id)
    if (idx < 0) throw new Error('staff user not found')
    const prev = demoUsers[idx]!
    const next: StaffUser = {
      ...prev,
      status: body.status ?? prev.status,
      bookmakerSessionFlag: body.bookmakerSessionFlag ?? prev.bookmakerSessionFlag,
      grants: body.grants ?? prev.grants,
      accessRoleName: body.accessRoleName ?? prev.accessRoleName,
      authorise: body.authorise ?? prev.authorise,
      hasPassword: body.password ? true : prev.hasPassword,
      updatedAt: new Date().toISOString(),
    }
    demoUsers = demoUsers.map((u, i) => (i === idx ? next : u))
    return next
  }
  const row = await apiFetch<StaffUser>(`/v1/staff-users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
  return { ...row, grants: grantsOf(row), authorise: authoriseOf(row) }
}
